export interface PolicyBullet {
  label?: string;
  text: string;
}

export interface PolicySection {
  id: string;
  title: string;
  paragraphs: string[];
  bullets?: PolicyBullet[];
}

export const ACKNOWLEDGEMENT_SECTION_ID = "acknowledgement";
export const ADMIN_SIGNATURE_SECTION_ID = "admin-signature";

export const COMPANY_SIGNATURE_PLACEHOLDER =
  "Company signature: _________________________  Date: _________________";

export const isPolicySignatureLine = (text: string) =>
  /^(Employee|Company) signature:/i.test(text.trim());

export const stripPolicySignatureLines = (paragraphs: string[]) =>
  paragraphs.filter((p) => !isPolicySignatureLine(p));

export const formatCompanySignatureLine = (
  signature: {
    signerName: string;
    signerTitle?: string;
    signedAt: string;
  } | null
) => {
  if (!signature) return COMPANY_SIGNATURE_PLACEHOLDER;

  const title = signature.signerTitle ? `, ${signature.signerTitle}` : "";
  const date = new Date(signature.signedAt).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
  return `Company signature: ${signature.signerName}${title}    Date: ${date}`;
};

export const buildDefaultAdminSignatureSection = (
  companyName: string
): PolicySection => ({
  id: ADMIN_SIGNATURE_SECTION_ID,
  title: "Company Administrator Signature",
  paragraphs: [
    `As company administrator, sign below to attest that you have reviewed and approve this Company Safety Policy for ${companyName}.`,
    "This attestation confirms the company administrator has reviewed and approved the published safety policy.",
    "I confirm I have reviewed this policy and authorize its publication for our organization.",
  ],
});

/** Aligned with https://onmway.com — 10 Protection Features */
export const ONMYWAY_SAFETY_FEATURES = [
  {
    name: "Archie+ SecurePath™",
    tagline: "Real Person, Real Time, Real Protection",
    description:
      "Safe-route guidance, geofences, zone alerts, timed check-ins, escort mode, and instant escalation to security or 911—so employees are not walking alone, unseen, or unheard.",
    compliance:
      "OSHA General Duty · Workplace Violence Prevention · Clery Act · Lone Worker (OSHA & DOL)",
  },
  {
    name: "Rideshare RideWatch™",
    tagline: "From Pickup to Drop-Off, We've Got You",
    description:
      "Driver identity checks, route-deviation alerts, hands-free panic workflows, evidence capture, and escalations for corporate rides and business travel.",
    compliance: "OSHA General Duty · DOL Duty-of-Care · Vicarious Liability Standards",
  },
  {
    name: "VerifyAI™",
    tagline: "Real or Fake? We Know",
    description:
      "Detects synthetic audio, video, and images; flags impersonation; and automates verification before funds transfer, access approvals, or high-risk actions.",
    compliance: "Fraud prevention · Executive identity protection · Audit-ready verification",
  },
  {
    name: "OnMyWay Drive™",
    tagline: "Powered by AI. Driven by Rewards.",
    description:
      "Auto-activates drive mode, detects screen interaction and phone motion, delivers hands-free AI assistance, and documents policy adherence to reduce crash risk.",
    compliance: "Distracted driving prevention · Fleet safety documentation",
  },
  {
    name: "Mental Health Shield™",
    tagline: "Protect Mental Health Before Stress Becomes a Crisis",
    description:
      "Early detection, guided support, confidential escalation pathways, and 24/7 emergency care with real people—reducing burnout, absenteeism, and legal exposure.",
    compliance: "Workplace wellness · Stress and burnout risk mitigation",
  },
  {
    name: "Archie+ Personal Assistant™",
    tagline: "On-Demand Live Assistant",
    description:
      "Safety-minded concierge for travel bookings, escort scheduling, venue pre-screening, and wellness support—driving easier living and safer days.",
    compliance: "Travel safety · Executive support workflows",
  },
  {
    name: "Safety In-Motion™",
    tagline: "Slip and Fall Prevention with Real-Time Response",
    description:
      "Smartphone motion sensing detects falls, slips, and unsafe walking behavior with instant alerts, escalations, and incident trails for claims and OSHA logs.",
    compliance: "OSHA recordkeeping · Slip/trip/fall prevention",
  },
  {
    name: "Remote-Worker Lifeline™",
    tagline: "Lone-Worker Protection When No One Is Around",
    description:
      "Presence and motion monitoring, SOS and deadman timers, auto-escalation, and location-aware workflows for isolated and field personnel.",
    compliance: "OSHA & DOL lone-worker protection · Duty of care",
  },
  {
    name: "Workplace Emergency Program™",
    tagline: "Immediate Action When Seconds Matter",
    description:
      "Real-time threat detection, panic workflows, instant routing to security or 911, and evidence packages for investigations.",
    compliance: "Workplace violence prevention · Active threat response",
  },
  {
    name: "OnMyWay Incentives™",
    tagline: "Real Rewards That Drive Safety",
    description:
      "Behavioral nudges and rewards for safe driving, walking, ride choices, and reporting—boosting adoption across the workforce.",
    compliance: "Safety engagement · Measurable participation records",
  },
];

export const ONMYWAY_PLATFORM_TAGLINE =
  "OnMyWay uses real people and advanced AI to deliver real-time protection from physical threats, digital threats, and compliance failures—at work and on the go.";

export const buildCompanySafetyPolicySections = (
  companyName: string
): PolicySection[] => [
  {
    id: "purpose",
    title: "1. Purpose",
    paragraphs: [
      `${companyName} ("the Company") is committed to protecting the health, safety, and wellbeing of every employee, contractor, and visitor. This Company Safety Policy establishes minimum standards aligned with federal workplace safety expectations, OSHA-informed training practices, insurance-aligned documentation, and FARS/DFARS readiness where applicable.`,
      ONMYWAY_PLATFORM_TAGLINE,
      `The Company partners with the OnMyWay Protection Platform to deliver mobile-first safety tools, training completion tracking, and readiness documentation. This policy is designed for employers adopting OnMyWay. Review with legal counsel before distribution.`,
    ],
  },
  {
    id: "scope",
    title: "2. Scope",
    paragraphs: [
      "This policy applies to all employees, supervisors, contractors, and authorized representatives performing work on behalf of the Company—including on-site, field, travel, remote/lone worker, and rideshare assignments.",
      "Coverage extends to walking, driving, flying, workplace activities, and third-party environments where the Company has a duty of care.",
    ],
  },
  {
    id: "responsibilities",
    title: "3. Roles & Responsibilities",
    paragraphs: [],
    bullets: [
      {
        label: "Executive leadership",
        text: "Provide resources, enforce accountability, and review safety performance and compliance readiness.",
      },
      {
        label: "Safety program administrators",
        text: "Maintain training records, company readiness certifications, weekly compliance reporting, and audit-ready documentation.",
      },
      {
        label: "Supervisors",
        text: "Conduct coaching, hazard identification, incident escalation, and verification of assigned safety tool usage.",
      },
      {
        label: "Employees",
        text: "Complete assigned Training Completion certifications, follow this policy, and use approved OnMyWay safety tools as configured.",
      },
    ],
  },
  {
    id: "platform",
    title: "4. OnMyWay Protection Platform — 10 Features",
    paragraphs: [
      "The Company deploys the following OnMyWay capabilities. Each feature maps to assignable employee training and company readiness documentation in the Employer Dashboard.",
    ],
    bullets: ONMYWAY_SAFETY_FEATURES.map(
      (f) => ({
        label: f.name,
        text: `${f.tagline}. ${f.description}`,
      })
    ),
  },
  {
    id: "walking-travel",
    title: "5. Walking & Travel Safety",
    paragraphs: [
      "Personnel assigned to campus, parking, client-site, or after-hours travel must use Archie+ SecurePath™ when walking in higher-risk contexts.",
      "Business rideshare and corporate travel must use Rideshare RideWatch™ from pickup through drop-off with documented escalation procedures.",
    ],
  },
  {
    id: "driving",
    title: "6. Distracted Driving & Fleet Safety",
    paragraphs: [
      "Use of handheld mobile devices while operating a vehicle on company time is prohibited except where permitted by law using approved hands-free methods.",
      "Employees shall use OnMyWay Drive™ drive mode, distraction detection, and safe-driving documentation tools where assigned.",
    ],
  },
  {
    id: "digital",
    title: "7. Digital Identity & Fraud Prevention",
    paragraphs: [
      "High-risk financial, access, and communications decisions must follow VerifyAI™ verification steps to detect deepfakes, impersonation, and social engineering.",
    ],
  },
  {
    id: "hazards",
    title: "8. Hazard Reporting & Incident Documentation",
    bullets: [
      {
        label: "Reporting",
        text: "Report hazards, near-misses, and injuries promptly via Safety In-Motion™ and employer incident workflows.",
      },
      {
        label: "Detection",
        text: "Use configured fall, slip, and motion alerts where assigned.",
      },
      {
        label: "Records",
        text: "Maintain OSHA-aligned incident documentation with time-stamped, location-verified records where available.",
      },
    ],
    paragraphs: [],
  },
  {
    id: "emergency",
    title: "9. Workplace Emergency & Threat Response",
    paragraphs: [
      "Workplace Emergency Program™ panic workflows, threat detection, and evidence collection procedures must be followed during emergencies.",
      "Remote-Worker Lifeline™ check-ins, SOS workflows, and deadman timers apply to designated lone-worker roles.",
    ],
  },
  {
    id: "wellness",
    title: "10. Mental Health & Employee Support",
    paragraphs: [
      "Employees may access Mental Health Shield™ resources, early-risk indicators, and confidential escalation pathways per Company guidelines.",
      "Supervisors shall refer distressed employees to approved support channels without retaliation.",
    ],
  },
  {
    id: "contractor",
    title: "11. Government Contractor & FARS/DFARS Readiness",
    paragraphs: [
      "Where the Company performs federal contract work, safety performance documentation shall align with FARS/DFARS 2026 expectations, effective FY2026, including FAR safety clause alignment.",
      "OnMyWay Readiness Certifications are internal documentation only—they are not government-issued credentials.",
    ],
  },
  {
    id: "training",
    title: "12. Employee & Company Certifications",
    paragraphs: [
      "Employees must complete assigned Training Completion certifications mapped to enabled platform features.",
      "The Company must maintain Readiness Certifications including policy publication, platform deployment, and FARS/DFARS reporting where applicable.",
    ],
  },
  {
    id: "insurance",
    title: "13. Insurance & Regulatory Alignment",
    paragraphs: [
      "The Company maintains safety programs to support insurance defensibility and regulatory alignment using OnMyWay participation records, incident trails, and documented duty-of-care measures.",
      "Complete Protection. Complete Compliance. — Get Covered. Get Compliant. Get OnMyWay.",
    ],
  },
  {
    id: ACKNOWLEDGEMENT_SECTION_ID,
    title: "14. Policy Acknowledgement",
    paragraphs: [
      `I acknowledge that I have received, read, and understand this ${companyName} Company Safety Policy and my assigned OnMyWay safety requirements.`,
    ],
  },
];
